import type { EnabledMfrrProductionUnit, MfrrBidResponse, MfrrDirection, MfrrMarket } from 'api/mfrr/mfrr.types'
import Datetime from 'utils/datetime/datetime'

/** Sentinel unit id for delivered bids that are not tied to a specific production unit. */
export const MFRR_NO_UNIT_ID = -1

const EAM_SLOT_MINUTES = 15
const CM_SLOT_MINUTES = 60

export type MfrrDeliveredBidUnit = {
  id: number
  name: string
}

export type MfrrDeliveredBidRow = {
  startTime: ISODateTime
  bidsByUnitId: Map<number, Partial<Record<MfrrDirection, MfrrBidResponse>>>
}

export function getLatestMfrrDeliveredAt(bids: MfrrBidResponse[]): ISODateTime | undefined {
  return bids.reduce<ISODateTime | undefined>((latest, bid) => {
    const deliveredAt = bid.delivered_at

    if (!deliveredAt || !Number.isFinite(Date.parse(deliveredAt))) {
      return latest
    }

    if (!latest || Date.parse(deliveredAt) > Date.parse(latest)) {
      return deliveredAt
    }

    return latest
  }, undefined)
}

export function getMfrrDeliveredBidUnits(
  bids: MfrrBidResponse[],
  units: EnabledMfrrProductionUnit[]
): MfrrDeliveredBidUnit[] {
  const unitIds = [...new Set(bids.map((bid) => bid.production_unit_id ?? MFRR_NO_UNIT_ID))]

  return unitIds
    .sort((first, second) => first - second)
    .map((id) => ({
      id,
      name: id === MFRR_NO_UNIT_ID ? '—' : (units.find((unit) => unit.id === id)?.display_name ?? String(id)),
    }))
}

export function getMfrrDeliveredBidRows(bids: MfrrBidResponse[]): MfrrDeliveredBidRow[] {
  const rows = new Map<ISODateTime, MfrrDeliveredBidRow>()

  for (const bid of bids) {
    const row = rows.get(bid.start_time) ?? { startTime: bid.start_time, bidsByUnitId: new Map() }
    const unitId = bid.production_unit_id ?? MFRR_NO_UNIT_ID
    const unitBids = row.bidsByUnitId.get(unitId) ?? {}

    unitBids[bid.direction] = bid
    row.bidsByUnitId.set(unitId, unitBids)
    rows.set(bid.start_time, row)
  }

  return [...rows.values()].sort(
    (first, second) => new Date(first.startTime).getTime() - new Date(second.startTime).getTime()
  )
}

export function getMfrrDeliveredBidPeriodLabel(startTime: ISODateTime, market: MfrrMarket): string {
  const slotMinutes = market === 'eam' ? EAM_SLOT_MINUTES : CM_SLOT_MINUTES
  const start = Datetime.toLocalTime(startTime, 'hour')
  const end = Datetime.toLocalTime(Datetime.addMinutes(startTime, slotMinutes), 'hour')

  return `${start}\u2013${end}`
}
